import React, { useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Loader from "src/components/Loader";
import useWindowSize from "src/hooks/useWindowSize";
import { LayoutVariants } from "src/api/types";
import ModalTabs from "src/components/Modal/ModalTabs";
import SideModal from "src/components/Modal/SideModal";
import { ModalViewContext } from "src/components/layout/ModalView";
import { useKeyPress } from "src/hooks/useKeyPress";
import ShadowBackground from "src/components/Modal/ShadowBackground";

export const MODAL_SIZES = {
	default: "max-w-[512px] md:min-w-[400px] max-h-[800px]",
	small: "max-w-[512px] md:min-w-[400px] h-[80vh] max-h-[800px]",
	medium: "max-w-[1080px] h-[85vh] sm:h-[80vh] sm:max-h-[800px] w-full md:w-[90%]",
	sidebar: "h-screen max-w-[1/2] w-full",
	side: "",
};

export type ModalSize = keyof typeof MODAL_SIZES;

export interface ModalProps {
	onClose: () => void;
	visible: boolean;
	children?: any;
	buttons?: any;
	type?: string;
	className?: string;
	bodyClassName?: string;
	title?: string;
	size: ModalSize;
	loading?: boolean;
	variant?: LayoutVariants;
	head: "default" | "floating" | "disabled";
	expanded?: boolean;
}

const Modal = (props: ModalProps) => {
	const modal = useContext(ModalViewContext);
	const {
		onClose,
		visible,
		children,
		buttons,
		className,
		bodyClassName,
		title,
		size,
		loading,
		variant: _variant,
		head,
	} = props;
	const { isPhone } = useWindowSize();
	const hasTitle = title || modal?.title;
	useKeyPress(["Escape"], () => {
		if (size !== "side" && visible) {
			onClose();
		}
	});
	const variants: any = {
		dark: {
			wrapper: "bg-dark-accent text-white",
			head: "bg-dark",
		},
		light: {
			wrapper: "bg-white text-dark",
			head: "border-b-[2px] border-gray-background bg-white z-[99]",
		},
	};
	const variant =
		variants[_variant === "white" ? "light" : _variant || "light"];

	if (size === "side") {
		return <SideModal {...props} />;
	}

	if (size === "sidebar") {
		return (
			<AnimatePresence>
				{visible && (
					<>
						<ShadowBackground onClick={onClose} />

						<motion.div
							transition={{
								ease: "linear",
							}}
							initial={{ x: 1000 }}
							animate={{ x: 0 }}
							exit={{ x: 1000 }}
							className={`z-[98] overflow-y-auto fixed w-full max-w-[600px] top-0 right-0 bottom-0 ${
								variant.wrapper
							} ${className || ""}`}
						>
							<div
								className={`flex flex-1 gap-4 flex-row ${
									head !== "floating"
										? variant.head
										: "absolute top-0 left-0 right-0 z-20"
								} rounded-t-md p-4 px-8 justify-between`}
							>
								{title || modal?.title ? (
									<strong className="text-[18px]">
										{title || modal?.title}
									</strong>
								) : (
									<span></span>
								)}
								<i
									onClick={onClose}
									className="fal fa-times text-2xl cursor-pointer"
								></i>
							</div>
							<div className="p-8 md:text-left overflow-auto text-center">
								{loading ? (
									<div className="flex flex-1 flex-col justify-center items-center">
										<Loader />
									</div>
								) : (
									children
								)}
							</div>
							{buttons && (
								<div className="modal-footer">
									<div className={``}>{buttons}</div>
								</div>
							)}
						</motion.div>
					</>
				)}
			</AnimatePresence>
		);
	}

	return (
		<AnimatePresence>
			{visible && (
				<>
					<ShadowBackground onClick={onClose} />
					<motion.div
						style={
							!isPhone
								? {
										top: "50%",
										left: "50%",
										transform: "translate(-50%, -50%)",
								  }
								: {}
						}
						className={`z-[98] flex flex-col flex-1 ${
							MODAL_SIZES[size]
						} fixed ${className || ""} ${
							isPhone && " bottom-0 left-0 right-0"
						}`}
					>
						<motion.div
							initial={{ opacity: 0.5, y: 100 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0.5, y: 100 }}
							transition={{
								duration: 0.2,
								bounce: 0,
							}}
							className={`${
								variant.wrapper
							} rounded-md flex-1 flex flex-col max-h-full ${
								isPhone && "h-full"
							}`}
						>
							{head !== "disabled" && (
								<div
									className={`flex gap-6 flex-row ${
										head !== "floating"
											? `${variant.head} sticky top-0`
											: `absolute top-0 right-0 z-20 ${
													hasTitle && "left-0"
											  }`
									} rounded-t-md p-4 px-6 justify-between`}
								>
									<div className="flex items-center gap-4">
										{hasTitle && (
											<strong className="text-[18px]">
												{title || modal?.title}
											</strong>
										)}
									</div>
									<i
										onClick={onClose}
										className="fal fa-times text-2xl cursor-pointer"
									></i>
								</div>
							)}
							<div
								className={`flex flex-1 md:text-left overflow-y-scroll flex-col ${
									bodyClassName || "p-6"
								}`}
							>
								{loading ? (
									<div className="flex flex-1 flex-col justify-center items-center">
										<Loader />
									</div>
								) : (
									children
								)}
							</div>
							{buttons && (
								<div className="modal-footer">
									<div className={``}>{buttons}</div>
								</div>
							)}
						</motion.div>
					</motion.div>
				</>
			)}
		</AnimatePresence>
	);
};

Modal.defaultProps = {
	head: "default",
	size: "default",
};

Modal.Tabs = ModalTabs;

interface ModalButtonsProps {
	children?: any;
	className?: string;
}

Modal.Buttons = ({ children, className }: ModalButtonsProps) => (
	<div
		className={`flex items-center justify-between bg-white dark:bg-dark-accent py-4 md:py-8 sticky bottom-0 ${
			className || ""
		}`}
	>
		{children}
	</div>
);

export default Modal;
